import { createRef, memo, useCallback, useMemo, useState } from 'react';
import { useSelector } from '@betty/toolkit/state';
import { selectAccount } from '@betty/account/state';
import {
  Box,
  Text,
  Input,
  Image,
  Button,
  KeyboardShift,
} from '@betty/moonlight';
import {
  buildErrors,
  buildInitialState,
  createFieldHandlers,
  formatAddress,
} from './AddressVerification.utils';
import { FIELDS, CONTENT_CONTAINER_STYLE } from './AddressVerification.content';
import {
  AddressVerificationField,
  AddressVerificationProps,
} from './AddressVerification.types';
import AccountService from '../../services/AccountService/AccountService';
import { TextInput } from 'react-native';

function AddressVerification({ onVerified }: AddressVerificationProps) {
  const account = useSelector(selectAccount);
  const [loading, setLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [state, setState] = useState(buildInitialState(account));
  const errors = useMemo(() => buildErrors(state), [state]);
  const fieldRefs = useMemo(() => FIELDS.map(() => createRef<TextInput>()), []);

  const onPress = useCallback(async () => {
    const hasError = Object.values(errors).filter(Boolean).length > 0;

    if (hasError) {
      setShowError(true);

      return;
    }

    const { building, street, unit, city, postalCode } = state;

    try {
      setLoading(true);

      await AccountService.verifyAddress({
        data: {
          addressLine1: formatAddress(building, street, unit),
          city,
          postalCode,
          provinceCode: 'ON',
          country: 'Canada',
        },
      });

      onVerified?.();
    } catch (error) {
      if (error instanceof Error) {
        // TODO: handle error
      }
    } finally {
      setLoading(false);
    }
  }, [state, errors, onVerified]);

  const onChange = useCallback(
    (value: string, field: AddressVerificationField) => {
      setState((currentState) => ({
        ...currentState,
        [field]: value,
      }));
    },
    [],
  );

  const onSubmitEditing = useCallback(
    (field: AddressVerificationField) => {
      const currentIndex = FIELDS.indexOf(field);

      if (currentIndex === FIELDS.length - 1) {
        onPress();

        return;
      }

      fieldRefs[currentIndex + 1].current?.focus();
    },
    [fieldRefs, onPress],
  );

  const handlers = useMemo(
    () => createFieldHandlers(onChange, onSubmitEditing),
    [onChange, onSubmitEditing],
  );

  return (
    <Box flex={1}>
      <KeyboardShift
        contentContainerStyle={CONTENT_CONTAINER_STYLE}
        additionalOffset={40}
      >
        <Box gap="space-400" alignItems="center">
          <Image name="onboarding.location" width={60} height={79} />
          <Text
            variant="h4"
            locKey="addressVerificationTitle"
            locNS="onboarding"
            color="darkSurfacePrimary"
            textAlign="center"
          />
          <Text
            variant="body-m"
            locKey="addressVerificationDescription"
            locNS="onboarding"
            color="darkSurfacePrimary"
          />
          <Box flex={1} gap="space-400" width="100%">
            {FIELDS.map((field, index) => (
              <Input
                key={field}
                ref={fieldRefs[index]}
                value={state[field]}
                placeholderKey={field}
                placeholderNS="onboarding"
                enterKeyHint={index === FIELDS.length - 1 ? 'go' : 'next'}
                error={showError ? errors[field] : undefined}
                onChangeText={handlers[field]?.onChange}
                onSubmitEditing={handlers[field]?.onSubmitEditing}
              />
            ))}
          </Box>
        </Box>
      </KeyboardShift>
      <Button
        locKey="submitAddress"
        locNS="onboarding"
        size="large"
        width="100%"
        leftIcon="check"
        paddingHorizontal="space-600"
        paddingBottom="safe-area-inset-bottom"
        loading={loading}
        onPress={onPress}
      />
    </Box>
  );
}

export default memo(AddressVerification);
